import { FC, useEffect } from 'react';

import { GenericErrorCard } from '@/components/data/error';
import { LoadingFullScreen } from '@/components/helpers/loading';
import { Invitation, useInvitationQuery } from '@/redux/back-dashboard-api';
import {
  setIsManager,
  setProfile,
  setRoles,
  setSession,
} from '@/redux/features/session/slice';
import { store } from '@/redux/store';
import {
  GetCurrentSessionResponse,
  GetRolesResponse,
  GetUserDirectReportsResponse,
  GetUserProfileResponse,
} from '@/typings/api';
import { useSession } from '@/redux/features/session/hooks';
import { getPrimaryCdsLocation } from '@/lib/utils/locations';

const getCurrentSessionResponse = (
  invitation: Invitation
): GetCurrentSessionResponse => ({
  user: {
    'https://accounts.ikea.com/email': invitation.email,
    'https://accounts.ikea.com/upn': invitation.email,
    'https://accounts.ikea.com/guid': '',
    'https://accounts.ikea.com/networkid': '',
    given_name: invitation.name,
    family_name: '',
    nickname: invitation.name,
    name: invitation.name,
    picture: '',
    updated_at: new Date().toISOString(),
    email: invitation.email,
    email_verified: true,
    sub: '',
    sid: '',
    manager: {
      displayName: '',
      id: '',
      mail: '',
    },
  },
  accessToken: '',
  accessTokenExpiresAt: Number.MAX_SAFE_INTEGER,
  idToken: '',
  idTokenExpiresAt: Number.MAX_SAFE_INTEGER,
});

const getRolesResponse = (invitation: Invitation): GetRolesResponse => ({
  accessToken: 'token roles',
  roles: [
    {
      id: 0,
      clientReference: invitation.location.atkomstClientId,
      name: 'CHECKA Location Viewer',
      service: 'CHECKA',
      description: 'Role for external user',
      permissions: invitation._orderId ? [] : [{ name: 'order.create' }],
    },
  ],
});

const getUserProfileResponse = (
  invitation: Invitation
): GetUserProfileResponse => ({
  id: invitation.email,
  displayName: invitation.name,
  givenName: invitation.name,
  surname: '',
  jobTitle: 'External user',
  mail: invitation.email,
  officeLocation: getPrimaryCdsLocation(invitation.location.cdsLocation)?.name,
  userPrincipalName: invitation.name,
});

const getUserDirectReportsResponse = (): GetUserDirectReportsResponse => ({
  value: [],
});

const RolesProvider: FC<{ invitation: Invitation }> = ({ invitation }) => {
  useEffect(() => {
    store.dispatch(setRoles(getRolesResponse(invitation).roles));
  }, [invitation]);

  return null;
};

const ProfileProvider: FC<{ invitation: Invitation }> = ({ invitation }) => {
  useEffect(() => {
    store.dispatch(setProfile(getUserProfileResponse(invitation)));
  }, [invitation]);

  return null;
};

const SessionProvider: FC<{ invitation: Invitation }> = ({ invitation }) => {
  useEffect(() => {
    store.dispatch(setSession(getCurrentSessionResponse(invitation)));
  }, [invitation]);

  return null;
};

const DirectReportsProvider: FC<{ invitation: Invitation }> = ({
  invitation,
}) => {
  useEffect(() => {
    store.dispatch(
      setIsManager(getUserDirectReportsResponse().value.length > 0)
    );
  }, [invitation]);

  return null;
};

export const InvitationAuthProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { invitationToken } = useSession();
  const {
    data: { invitation } = {},
    isLoading,
    error,
  } = useInvitationQuery(
    {
      invitationId: invitationToken || '',
    },
    { skip: !invitationToken }
  );

  if (isLoading) {
    return <LoadingFullScreen />;
  }

  if (error || !invitation || !invitationToken) {
    return (
      <GenericErrorCard
        error={{
          name: 'InvalidInvitation',
          message: 'The invitation is not valid or has been expired.',
        }}
      />
    );
  }

  return (
    <>
      <SessionProvider invitation={invitation} />
      <ProfileProvider invitation={invitation} />
      <DirectReportsProvider invitation={invitation} />
      <RolesProvider invitation={invitation} />
      {children}
    </>
  );
};
